import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import GroupsIcon from '@mui/icons-material/Groups'
import AddIcon from '@mui/icons-material/Add'
import SettingsIcon from '@mui/icons-material/Settings'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'
import { setWorkspace } from '../features/org/orgSlice'
import CreateOrgDialog from './CreateOrgDialog'

function WorkspaceSwitcher() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [anchorEl, setAnchorEl] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)

  const { myOrgs, activeWorkspace } = useSelector((state) => state.org)

  const activeOrg = myOrgs.find((o) => o.slug === activeWorkspace)
  const label = activeWorkspace === 'personal' ? 'Personal' : activeOrg?.name || 'Personal'

  const close = () => setAnchorEl(null)

  const pick = (workspace) => {
    if (workspace === activeWorkspace) {
      close()
      return
    }
    dispatch(setWorkspace(workspace))
    close()

    // Leave any page that's tied to the workspace you're switching away from.
    const path = location.pathname
    if (path.startsWith('/editor')) {
      navigate('/dashboard')
    } else if (path.startsWith('/orgs/')) {
      navigate(workspace === 'personal' ? '/dashboard' : `/orgs/${workspace}`)
    }
    // On /dashboard (refetches itself), /account, / etc. — stay put.
  }

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={activeWorkspace === 'personal' ? <PersonIcon /> : <GroupsIcon />}
        endIcon={<ExpandMoreIcon />}
        sx={{
          color: '#FFFFFF',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          textTransform: 'none',
          maxWidth: 220,
          '& .MuiButton-startIcon': { mr: 0.75 },
          '& .MuiSvgIcon-root': { color: '#FFFFFF' },
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
        }}
      >
        <Typography noWrap sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#FFFFFF' }}>
          {label}
        </Typography>
      </Button>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block' }}>
          Workspace
        </Typography>

        <MenuItem selected={activeWorkspace === 'personal'} onClick={() => pick('personal')}>
          <ListItemIcon>
            {activeWorkspace === 'personal' ? <CheckIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>Personal</ListItemText>
        </MenuItem>

        {myOrgs.map((org) => (
          <MenuItem
            key={org._id}
            selected={activeWorkspace === org.slug}
            onClick={() => pick(org.slug)}
          >
            <ListItemIcon>
              {activeWorkspace === org.slug ? <CheckIcon fontSize="small" /> : <GroupsIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={org.name} secondary={org.role} />
          </MenuItem>
        ))}

        <Divider />

        {activeOrg && (
          <MenuItem
            onClick={() => {
              close()
              navigate(`/orgs/${activeOrg.slug}`)
            }}
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Manage {activeOrg.name}</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            close()
            setCreateOpen(true)
          }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create organisation…</ListItemText>
        </MenuItem>
      </Menu>

      <CreateOrgDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  )
}

export default WorkspaceSwitcher
